import { useState, useEffect } from 'react';
import { ExternalLink, Hash, Bot, Users, Wifi, WifiOff, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SlackIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.163 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.163 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.163 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.27a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.315A2.528 2.528 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.523h-6.315z"/>
  </svg>
);

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

export function WorkspaceLaunchCard({ compact = false }) {
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkspaceData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/workspace/slack-connection`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setWorkspaceData(data);
        }
      } catch (error) {
        console.error('Error fetching workspace data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkspaceData();
  }, []);

  const slackData = workspaceData?.data;

  // Always default to connected and live
  const isConnected = true;
  const channelName = '#client-my-company-name';

  const handleOpenWorkspace = () => {
    window.open('https://aihorsepowere-iss2965.slack.com', '_blank');
  };

  if (loading) {
    return (
      <div className={cn(
        "rounded-2xl bg-white/[0.03] border border-white/10 animate-pulse",
        compact ? "p-4" : "p-6"
      )}>
        <div className="h-16 bg-white/5 rounded-xl" />
      </div>
    );
  }

  // Compact version for Dashboard
  if (compact) {
    return (
      <button
        onClick={handleOpenWorkspace}
        className="glass rounded-2xl p-5 hover:border-purple-500/30 transition-all cursor-pointer group h-full relative overflow-hidden text-left w-full"
        data-testid="dashboard-open-workspace-btn"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative z-10">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <SlackIcon className="w-6 h-6 text-purple-400" />
          </div>
          <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">Open My Workspace</h3>
          <p className="text-sm text-gray-400">
            {isConnected ? 'My Connected Slack Workspace' : 'Connect Slack workspace'}
          </p>
          <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-primary absolute top-5 right-5 group-hover:translate-x-1 transition-all" />
        </div>
      </button>
    );
  }

  // Full version for AGI Workhorse page
  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6 relative overflow-hidden group hover:border-purple-500/20 transition-all"
      data-testid="workspace-launch-card"
    >
      {/* Subtle glow */}
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
      
      <div className="relative z-10">
        {/* Card Header */}
        <div className="flex items-start gap-4 mb-5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/20 flex items-center justify-center shrink-0">
            <SlackIcon className="w-7 h-7 text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-heading font-bold text-white">AI Workspace Connection</h3>
            <p className="text-sm text-gray-400">Access your AI Horse-Powered Workspace inside Slack.</p>
          </div>
          {/* Status Badge */}
          <div className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0",
            isConnected 
              ? "bg-green-500/10 text-green-400 border border-green-500/20" 
              : "bg-gray-500/10 text-gray-400 border border-gray-500/20"
          )}>
            {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isConnected ? 'Connected' : 'Not Connected'}
          </div>
        </div>

        {/* Data Fields Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {/* Workspace Status */}
          <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Workspace Status</p>
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", isConnected ? "bg-green-400" : "bg-gray-500")} />
              <span className={cn("text-sm font-medium", isConnected ? "text-green-400" : "text-gray-400")}>
                {isConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
          </div>

          {/* Primary Channel */}
          <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Primary Channel</p>
            <div className="flex items-center gap-2">
              <Hash className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-sm text-white font-medium truncate">
                {channelName}
              </span>
            </div>
          </div>

          {/* AI Horse-Powered Bot */}
          <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">AI Horse-Powered Bot</p>
            <div className="flex items-center gap-2">
              <Bot className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-sm font-medium text-green-400">Active</span>
            </div>
          </div>

          {/* Manus Task Bot */}
          <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Manus Task Bot</p>
            <div className="flex items-center gap-2">
              <Bot className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-sm font-medium text-green-400">Active</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Button
          onClick={handleOpenWorkspace}
          className={cn(
            "w-full sm:w-auto px-8 py-5 text-base font-bold shadow-lg transition-all",
            isConnected
              ? "bg-purple-600 hover:bg-purple-700 text-white shadow-purple-500/20 hover:shadow-purple-500/30"
              : "bg-primary hover:bg-primary/90 text-black shadow-primary/20"
          )}
          data-testid="open-workspace-btn"
        >
          <SlackIcon className="w-5 h-5 mr-2" />
          {isConnected ? 'Enter Slack' : 'Connect Slack Workspace'}
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
